/* eslint-disable @typescript-eslint/naming-convention */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import LogEntry from '@components/LogEntry';
import LoadingSpinner from '@components/LoadingSpinner';
import { useLogTail } from '@hooks/useLogTail';
import { logsApi } from '@api/logs';
import { useLogStore } from '@/stores/logStore';
import { useMuteStore } from '@/stores/muteStore';
import { useTimeRangeStore } from '@/stores/timeRangeStore';
import { isWithinLiveTailWindow } from '@/utils/liveTail';
import { describeMuteRule, getMuteRuleValueFromLog, isLogMuted } from '@/utils/muteRules';
import type { LogEntry as LogRecord, LogFilters, LogWindowResponse } from '../types/index';

interface LogsPageProps {
    filters: LogFilters;
    onFiltersChange: (filters: LogFilters) => void;
}

function LogsPage({ filters, onFiltersChange }: LogsPageProps) {
    const parentRef = React.useRef<HTMLDivElement>(null);
    const [isInitialLoad, setIsInitialLoad] = React.useState(true);
    const [loadDirection, setLoadDirection] = React.useState<'older' | null>(null);

    const {
        logs,
        logIds,
        expandedLogIds,
        scrollAnchor,
        newestCursor,
        oldestCursor,
        newLogsCount,
        scopeId,
        mergeNewLogs,
        prependOldLogs,
        toggleExpanded,
        setScrollAnchor,
        setNewLogsCount,
        resetNewLogsCount,
        reset,
        isExpanded
    } = useLogStore();

    const { rules: muteRules, showMuted, addRule, removeRule, clearRules, setShowMuted } = useMuteStore();
    const { followNow, setFollowNow } = useTimeRangeStore();

    const effectiveFilters = React.useMemo(() => {
        if (!followNow) return filters;

        const nextFilters = { ...filters };
        delete nextFilters.dateTo;
        return nextFilters;
    }, [filters, followNow]);

    const visibleLogIds = React.useMemo(() => {
        if (showMuted) return logIds;

        const nextIds: string[] = [];
        for (const id of logIds) {
            const log = logs.get(id);
            if (!log) continue;
            if (!isLogMuted(log, muteRules)) nextIds.push(id);
        }
        return nextIds;
    }, [logIds, logs, muteRules, showMuted]);

    const mutedCount = React.useMemo(() => logIds.length - visibleLogIds.length, [logIds.length, visibleLogIds.length]);
    const historicalEndLabel = React.useMemo(() => {
        if (!filters.dateTo) return null;

        const parsed = new Date(filters.dateTo);
        if (isNaN(parsed.getTime())) return null;

        return parsed.toLocaleString();
    }, [filters.dateTo]);

    const toggleFollowNow = React.useCallback(
        (nextFollowNow: boolean) => {
            setFollowNow(nextFollowNow);

            if (!nextFollowNow && isWithinLiveTailWindow(filters.dateTo)) {
                onFiltersChange({
                    ...filters,
                    dateTo: new Date().toISOString()
                });
            }
        },
        [filters, onFiltersChange, setFollowNow]
    );

    const resumeLiveTail = React.useCallback(() => {
        setFollowNow(true);
    }, [setFollowNow]);

    // Reset store when effective filters change (includes Live Tail toggle)
    React.useEffect(() => {
        reset();
        setIsInitialLoad(true);
    }, [effectiveFilters, reset]);

    // Initial load query
    const initialQuery = useQuery<LogWindowResponse>({
        queryKey: ['logs-window-initial', effectiveFilters, followNow, scopeId],
        queryFn: async ({ signal }) => logsApi.getLogWindow(effectiveFilters, { direction: 'latest', limit: 100 }, signal),
        enabled: isInitialLoad,
        retry: 2,
        refetchOnWindowFocus: false
    });

    const { isLoading: isInitialLoading, error: initialError } = initialQuery;

    React.useEffect(() => {
        const result = initialQuery.data;
        if (!result) return;
        if (useLogStore.getState().scopeId !== scopeId) return;

        mergeNewLogs(result.logs);
        setIsInitialLoad(false);
    }, [initialQuery.data, mergeNewLogs, scopeId]);

    const handleIncomingLogs = React.useCallback(
        (incomingLogs: LogRecord[]) => {
            if (incomingLogs.length === 0) return;
            if (useLogStore.getState().scopeId !== scopeId) return;

            mergeNewLogs(incomingLogs);

            if (parentRef.current && parentRef.current.scrollTop > 100) {
                const visibleNewLogsCount = showMuted
                    ? incomingLogs.length
                    : incomingLogs.filter((log) => !isLogMuted(log, muteRules)).length;

                if (visibleNewLogsCount > 0) {
                    const currentCount = useLogStore.getState().newLogsCount;
                    setNewLogsCount(currentCount + visibleNewLogsCount);
                }
            }
        },
        [mergeNewLogs, muteRules, scopeId, setNewLogsCount, showMuted]
    );

    const {
        error: tailError,
        isCatchingUp: isBackgroundCatchUp,
        isFetching: isFetchingNew
    } = useLogTail({
        cursor: newestCursor,
        enabled: followNow && !isInitialLoad,
        filters: effectiveFilters,
        isPaused: !!loadDirection,
        onLogs: handleIncomingLogs
    });

    // Query for health check
    const { data: health } = useQuery({
        queryKey: ['health'],
        queryFn: ({ signal }) => logsApi.checkHealth(signal),
        refetchInterval: 30000,
        retry: 1
    });

    // Virtual scrolling setup
    const rowVirtualizer = useVirtualizer({
        count: visibleLogIds.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 100, // Base estimate, will be measured dynamically
        overscan: 5,
        getItemKey: React.useCallback((index: number) => visibleLogIds[index], [visibleLogIds])
    });

    // Force virtualizer to recalculate when expanded state changes
    React.useEffect(() => {
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
            rowVirtualizer.measure();
        });
    }, [expandedLogIds.size, rowVirtualizer]);

    // Track scroll position for preservation
    const updateScrollAnchor = React.useCallback(() => {
        if (!parentRef.current || visibleLogIds.length === 0) return;

        const container = parentRef.current;
        const scrollTop = container.scrollTop;

        // Find the log at the top of the viewport
        const items = rowVirtualizer.getVirtualItems();
        for (const virtualItem of items) {
            if (virtualItem.start <= scrollTop && virtualItem.end > scrollTop) {
                setScrollAnchor({
                    logId: visibleLogIds[virtualItem.index],
                    offset: scrollTop - virtualItem.start
                });
                break;
            }
        }
    }, [rowVirtualizer, setScrollAnchor, visibleLogIds]);

    // Restore scroll position after updates
    const restoreScrollPosition = React.useCallback(() => {
        if (!scrollAnchor || !parentRef.current) return;

        const anchorIndex = visibleLogIds.indexOf(scrollAnchor.logId);
        if (anchorIndex === -1) return;

        const items = rowVirtualizer.getVirtualItems();
        const anchorItem = items.find((item) => item.index === anchorIndex);

        if (anchorItem) {
            const newScrollTop = anchorItem.start + scrollAnchor.offset;
            parentRef.current.scrollTop = newScrollTop;
        }
    }, [scrollAnchor, rowVirtualizer, visibleLogIds]);

    // Preserve scroll on log updates
    React.useEffect(() => {
        if (scrollAnchor) {
            restoreScrollPosition();
        }
    }, [logs.size, restoreScrollPosition, scrollAnchor]);

    // Infinite scrolling for older logs
    const handleScroll = React.useCallback(() => {
        if (!parentRef.current || loadDirection) return;

        const { scrollTop, scrollHeight, clientHeight } = parentRef.current;

        // Load older logs when near bottom
        if (scrollHeight - scrollTop - clientHeight < 500 && oldestCursor) {
            setLoadDirection('older');

            logsApi
                .getLogWindow(effectiveFilters, { cursor: oldestCursor, direction: 'older', limit: 50 })
                .then((result) => {
                    if (result.logs.length > 0) {
                        if (useLogStore.getState().scopeId !== scopeId) return;
                        updateScrollAnchor();
                        prependOldLogs(result.logs);
                    }
                })
                .catch((error) => {
                    console.error('Failed to load older logs:', error);
                })
                .finally(() => {
                    setLoadDirection(null);
                });
        }
    }, [effectiveFilters, loadDirection, oldestCursor, prependOldLogs, scopeId, updateScrollAnchor]);

    // Debounced scroll handler
    React.useEffect(() => {
        const container = parentRef.current;
        if (!container) return;

        let scrollTimeout: ReturnType<typeof setTimeout>;
        const debouncedScroll = () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                updateScrollAnchor();
                handleScroll();

                // Reset new logs count when scrolled to top
                if (container.scrollTop < 50 && newLogsCount > 0) {
                    resetNewLogsCount();
                }
            }, 100);
        };

        container.addEventListener('scroll', debouncedScroll);
        return () => {
            container.removeEventListener('scroll', debouncedScroll);
            clearTimeout(scrollTimeout);
        };
    }, [updateScrollAnchor, handleScroll, newLogsCount, resetNewLogsCount]);

    if (isInitialLoading) {
        return <LoadingSpinner message="Loading logs from ClickHouse..." />;
    }

    if (initialError) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center max-w-md">
                    <i className="fas fa-exclamation-triangle text-danger text-4xl mb-4" />
                    <h2 className="text-xl font-semibold text-white mb-2">Error Loading Logs</h2>
                    <p className="text-zinc-400 mb-4">
                        {initialError instanceof Error ? initialError.message : 'Failed to fetch log data from ClickHouse.'}
                    </p>
                    <button
                        onClick={() => {
                            reset();
                            setIsInitialLoad(true);
                        }}
                        className="btn btn-secondary mt-4"
                    >
                        <i className="fas fa-sync-alt mr-2" />
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            {/* Page Header */}
            <div className="bg-dark border-b border-zinc-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center space-x-3">
                            <h1 className="text-2xl font-bold text-white">Log Entries</h1>

                            {/* Status indicators */}
                            {(isFetchingNew || isBackgroundCatchUp) && (
                                <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                                    <span className="text-xs text-blue-400">
                                        {isBackgroundCatchUp ? 'Catching up...' : 'Refreshing...'}
                                    </span>
                                </div>
                            )}

                            {/* Connection Status */}
                            {health && (
                                <div className="flex items-center space-x-2">
                                    <div
                                        className={`w-2 h-2 rounded-full ${
                                            health.status === 'healthy' ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                                        }`}
                                    />
                                    <span className={`text-xs ${health.status === 'healthy' ? 'text-green-500' : 'text-red-500'}`}>
                                        {health.status === 'healthy' ? 'Connected' : 'Disconnected'}
                                    </span>
                                </div>
                            )}

                            {tailError && followNow && (
                                <div className="flex items-center space-x-2" title={tailError.message}>
                                    <div className="w-2 h-2 rounded-full bg-amber-500" />
                                    <span className="text-xs text-amber-400">Live tail retrying</span>
                                </div>
                            )}
                        </div>
                        <p className="text-sm text-zinc-400 mt-1">
                            {visibleLogIds.length} shown
                            {mutedCount > 0 && !showMuted && <span className="ml-2">• {mutedCount} muted</span>}
                            <span className="ml-2">• {logIds.length} loaded</span>
                            {logs.size > 0 && expandedLogIds.size > 0 && <span className="ml-2">• {expandedLogIds.size} expanded</span>}
                        </p>

                        {muteRules.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                                <span className="text-xs text-zinc-500 mr-1">Muted:</span>
                                {muteRules.map((rule) => {
                                    const { label, title } = describeMuteRule(rule);
                                    return (
                                        <button
                                            key={rule.id}
                                            onClick={() => removeRule(rule.id)}
                                            className="inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs bg-zinc-700 text-zinc-100 hover:bg-zinc-600 transition-colors"
                                            title={`${title} (click to remove)`}
                                            type="button"
                                        >
                                            <i className="fas fa-times text-zinc-300" />
                                            <span className="max-w-[520px] truncate">{label}</span>
                                        </button>
                                    );
                                })}
                                <button
                                    onClick={() => clearRules()}
                                    className="inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs bg-zinc-800 text-zinc-200 hover:bg-zinc-700 transition-colors"
                                    title="Clear all mutes"
                                    type="button"
                                >
                                    <i className="fas fa-trash-alt" />
                                    Clear
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center space-x-4">
                        {/* New Logs Indicator */}
                        {newLogsCount > 0 && (
                            <button
                                onClick={() => {
                                    parentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                                    resetNewLogsCount();
                                }}
                                className="btn btn-primary btn-sm animate-pulse"
                            >
                                <i className="fas fa-arrow-up mr-2" />
                                {newLogsCount} new {newLogsCount === 1 ? 'log' : 'logs'}
                            </button>
                        )}

                        {/* Live Tail Toggle */}
                        <button
                            onClick={() => toggleFollowNow(!followNow)}
                            className={`btn btn-secondary btn-sm ${followNow ? 'opacity-100' : 'opacity-70'}`}
                            title={followNow ? 'Live tail is enabled (end time ignored)' : 'Live tail is disabled (end time honored)'}
                            type="button"
                        >
                            <i className={`fas ${followNow ? 'fa-bolt' : 'fa-pause'}`} />
                        </button>

                        {/* Show Muted Toggle */}
                        {muteRules.length > 0 && (
                            <button
                                onClick={() => setShowMuted(!showMuted)}
                                className="btn btn-secondary btn-sm"
                                title={showMuted ? 'Hide muted logs' : 'Show muted logs'}
                                type="button"
                            >
                                <i className={`fas ${showMuted ? 'fa-eye' : 'fa-eye-slash'}`} />
                            </button>
                        )}

                        {/* Refresh Button */}
                        <button
                            onClick={() => {
                                reset();
                                setIsInitialLoad(true);
                            }}
                            className="btn btn-secondary btn-sm"
                            title="Reload all logs"
                            disabled={isInitialLoading}
                        >
                            <i className="fas fa-sync-alt" />
                        </button>
                    </div>
                </div>
            </div>

            {!followNow && (
                <div className="border-b border-amber-800 bg-amber-950/60 px-6 py-3">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-amber-300">
                                Historical mode is active. Automatic loading is paused.
                            </div>
                            <div className="text-xs text-amber-200/80 mt-1">
                                {historicalEndLabel
                                    ? `This view is capped at ${historicalEndLabel}. Move the end time near now or resume live tail to keep loading new rows.`
                                    : 'This view is using a fixed snapshot. Resume live tail to keep loading new rows.'}
                            </div>
                        </div>
                        <div className="shrink-0">
                            <button onClick={resumeLiveTail} className="btn btn-secondary btn-sm" type="button">
                                <i className="fas fa-bolt mr-2" />
                                Resume Live Tail
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Virtual Scrolling Container */}
            <div ref={parentRef} className="flex-1 overflow-y-auto relative">
                {visibleLogIds.length > 0 ? (
                    <div
                        style={{
                            height: `${rowVirtualizer.getTotalSize()}px`,
                            width: '100%',
                            position: 'relative'
                        }}
                    >
                        {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                            const logId = visibleLogIds[virtualItem.index];
                            const log = logs.get(logId);

                            if (!log) return null;

                            return (
                                <div
                                    key={virtualItem.key}
                                    data-log-id={logId}
                                    data-index={virtualItem.index}
                                    ref={rowVirtualizer.measureElement}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        transform: `translateY(${virtualItem.start}px)`
                                    }}
                                >
                                    <LogEntry
                                        log={log}
                                        expanded={isExpanded(logId)}
                                        onToggleExpand={() => {
                                            updateScrollAnchor();
                                            toggleExpanded(logId);
                                        }}
                                        onMute={(kind) => {
                                            const value = getMuteRuleValueFromLog(kind, log);
                                            if (!value) return;
                                            addRule(kind, value);
                                        }}
                                    />
                                </div>
                            );
                        })}

                        {/* Loading indicator for infinite scroll */}
                        {loadDirection === 'older' && (
                            <div className="absolute bottom-0 left-0 right-0 bg-dark p-4 text-center">
                                <i className="fas fa-spinner fa-spin text-zinc-400" />
                                <span className="ml-2 text-sm text-zinc-400">Loading older logs...</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            {logIds.length > 0 && mutedCount > 0 && !showMuted ? (
                                <>
                                    <i className="fas fa-eye-slash text-zinc-600 text-4xl mb-4" />
                                    <h2 className="text-xl font-semibold text-zinc-400 mb-2">All Logs Muted</h2>
                                    <p className="text-zinc-500 mb-4">Show muted logs or remove a mute rule.</p>
                                    <div className="flex items-center justify-center gap-2">
                                        <button onClick={() => setShowMuted(true)} className="btn btn-secondary btn-sm" type="button">
                                            <i className="fas fa-eye mr-2" />
                                            Show muted
                                        </button>
                                        <button onClick={() => clearRules()} className="btn btn-secondary btn-sm" type="button">
                                            <i className="fas fa-trash-alt mr-2" />
                                            Clear mutes
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-search text-zinc-600 text-4xl mb-4" />
                                    <h2 className="text-xl font-semibold text-zinc-400 mb-2">No Logs Found</h2>
                                    <p className="text-zinc-500 mb-4">Try adjusting your filters or search criteria.</p>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LogsPage;
